import { useState } from "react";
import { LockKeyhole } from "lucide-react";
import type { ResourceDraft, ResourceWizardApi } from "../ResourceWizard";
import { InstructionSteps } from "./WizardUi";

type Props = {
  api: ResourceWizardApi;
  draft: ResourceDraft;
  onBack: () => void;
  onNext: (patch: Partial<ResourceDraft>) => Promise<void>;
};

export function Step4BotStat({ api, draft, onBack, onNext }: Props) {
  if (draft.bot_verification_method === "token") {
    return (
      <BotTokenStep api={api} draft={draft} onBack={onBack} onNext={onNext} />
    );
  }
  return (
    <BotMembersStep api={api} draft={draft} onBack={onBack} onNext={onNext} />
  );
}

function BotTokenStep({ api, draft, onNext }: Props) {
  const [token, setToken] = useState("");
  const [status, setStatus] = useState<"idle" | "running" | "ok" | "error">(
    draft.bot_token_verified_at ? "ok" : "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const valid = token.trim().length >= 20;

  async function runVerify() {
    if (!draft.id) return;
    const trimmed = token.trim();
    if (!trimmed) {
      setStatus("error");
      setErrorMessage("введите токен");
      return;
    }
    setStatus("running");
    setErrorMessage(null);
    try {
      const patchRes = await api.patchResource(draft.id, {
        bot_token: trimmed,
        verification_mode: "verified",
      });
      if (patchRes.error) {
        setStatus("error");
        setErrorMessage("ключ неверный!");
        return;
      }
      const verifyRes = await api.verifyResource(draft.id);
      if (verifyRes.error) {
        setStatus("error");
        setErrorMessage(formatBotMembersError(verifyRes.error));
        return;
      }
      setStatus("ok");
    } catch {
      setStatus("error");
      setErrorMessage("ключ неверный!");
    }
  }

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <InstructionSteps
        steps={[
          {
            text: "откройте @botfather в telegram, введите команду /mybots и выберите нужного бота",
          },
          {
            text: "нажмите на кнопку «api token», скопируйте его и вставьте сюда",
          },
          {
            text: (
              <>
                <span>нажмите </span>
                <button
                  type="button"
                  className="tp-resource-inline-action"
                  onClick={runVerify}
                  disabled={status === "running" || !valid}
                >
                  {status === "running" ? "проверяю..." : "проверить"}
                </button>
              </>
            ),
          },
        ]}
      />
      <div className="tp-resource-token-card">
        <input
          type="text"
          className="tp-input"
          placeholder="вставьте api token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="one-time-code"
          inputMode="text"
          spellCheck={false}
          disabled={status === "running"}
        />
        <div className="tp-resource-token-note">
          <LockKeyhole className="tp-resource-token-note-icon" aria-hidden="true" />
          <span>
            токен хранится в зашифрованном виде и используется только для
            проверки запуска
          </span>
        </div>
      </div>
      {status === "ok" && (
        <p className="tp-resource-check-status text-sm text-green-600">
          успешно!
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600">{errorMessage}</p>
      )}
      <button
        onClick={() =>
          onNext({
            botstat_code: null,
            verification_mode: "verified",
            bot_token_configured: true,
          })
        }
        disabled={status !== "ok"}
        className="tp-button-primary w-full disabled:opacity-50"
      >
        далее
      </button>
    </div>
  );
}

function formatBotMembersError(error: unknown): string {
  const payload = error as
    | { code?: string; detail?: string; error?: { code?: string } }
    | null
    | undefined;
  const code = payload?.error?.code ?? payload?.code ?? payload?.detail;
  if (code === "botstat_bot_mismatch") {
    return "этот ключ относится к другому боту";
  }
  if (code === "bot_token_bot_mismatch") {
    return "этот токен относится к другому боту";
  }
  if (code === "botstat_unavailable") {
    return "сервис проверки временно недоступен, попробуйте еще раз";
  }
  if (code === "botstat_payment_required") {
    return "ключ не принят, активируйте проверку в @botmembersrobot или проверьте оплату";
  }
  if (code === "botstat_code_invalid") {
    return "ключ не принят, проверьте ключ в @botmembersrobot";
  }
  return "ключ неверный!";
}

function BotMembersStep({ api, draft, onNext }: Props) {
  const [code, setCode] = useState(draft.botstat_code ?? "");
  const [status, setStatus] = useState<"idle" | "running" | "ok" | "error">(
    draft.botstat_verified_at ? "ok" : "idle",
  );
  const [verifiedAt, setVerifiedAt] = useState<string | null>(
    draft.botstat_verified_at,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function runVerify() {
    if (!draft.id) return;
    const trimmed = code.trim();
    if (!trimmed) {
      setStatus("error");
      setErrorMessage("введите ключ");
      return;
    }
    setStatus("running");
    setErrorMessage(null);
    try {
      const patchRes = await api.patchResource(draft.id, {
        botstat_code: trimmed,
      });
      if (patchRes.error) {
        setStatus("error");
        setErrorMessage(formatBotMembersError(patchRes.error));
        return;
      }
      const verifyRes = await api.verifyResource(draft.id);
      if (verifyRes.error) {
        setStatus("error");
        setErrorMessage(formatBotMembersError(verifyRes.error));
        return;
      }
      const data = verifyRes.data as
        | { verified_at?: string }
        | null
        | undefined;
      setVerifiedAt(data?.verified_at ?? new Date().toISOString());
      setStatus("ok");
    } catch {
      setStatus("error");
      setErrorMessage("ключ неверный!");
    }
  }

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <InstructionSteps
        steps={[
          {
            text: "откройте @botmembersrobot в telegram, добавьте своего бота по инструкции",
          },
          { text: "нажмите на «ключ», скопируйте его и вставьте сюда" },
          {
            text: (
              <>
                <span>нажмите </span>
                <button
                  type="button"
                  className="tp-resource-inline-action"
                  onClick={runVerify}
                  disabled={status === "running" || !code.trim()}
                >
                  {status === "running" ? "проверяю..." : "проверить"}
                </button>
              </>
            ),
          },
        ]}
      />
      <div className="tp-resource-token-card">
        <input
          type="text"
          className="tp-input"
          placeholder="вставьте ключ"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={status === "running"}
        />
        <div className="tp-resource-token-note">
          <LockKeyhole className="tp-resource-token-note-icon" aria-hidden="true" />
          <span>
            ключ хранится в зашифрованном виде и используется только для
            проверки запуска
          </span>
        </div>
      </div>
      {status === "ok" && (
        <p className="tp-resource-check-status text-sm text-green-600">
          успешно!
        </p>
      )}
      {status === "error" && (
        <p className="text-sm text-red-600">{errorMessage}</p>
      )}
      <button
        onClick={() =>
          onNext({
            verification_mode: "verified",
            botstat_verified_at:
              verifiedAt ??
              draft.botstat_verified_at ??
              new Date().toISOString(),
          })
        }
        disabled={status !== "ok"}
        className="tp-button-primary w-full disabled:opacity-50"
      >
        далее
      </button>
    </div>
  );
}
